import { PAGE_SUB_TYPES } from "@sparefoot/tracking-events";

/**
 * Build the canonical URL for a PSP page based on the keyword
 * Matches the route patterns in routes.js
 * @param {string} keyword - The original keyword from the route (e.g., 'rv-storage-units')
 * @returns {string} The full URL
 */
export const buildPSPUrl = (keyword) => {
	if (!keyword) {
		return "";
	}

	const baseUrl = "https://www.storage.com/storage-solutions";

	// Default pattern: /storage-solutions/{keyword}-storage-units
	return `${baseUrl}/${keyword}`;
};

/**
 * Build review schema from reviews in pageContent
 * @param {object} pageContent - The page content object with reviews property
 * @returns {array} Array of review schema objects
 */
export const buildReviewSchema = (pageContent) => {
	// Find the reviews module in sections and extract testimonials
	const reviewsModule = pageContent?.sections
		?.flatMap((section) => section.modules || [])
		?.find((module) => module.type === "reviews");

	const reviews = reviewsModule?.props?.testimonials ?? [];

	return reviews.map((review) => ({
		"@type": "Review",
		author: {
			"@type": "Person",
			name: review.reviewerName || review.author || ""
		},
		reviewRating: {
			"@type": "Rating",
			ratingValue: review.stars || review.reviewRating || "5",
			bestRating: "5",
			worstRating: "1"
		},
		reviewBody: review.text || review.reviewText || review.body || ""
	}));
};

/**
 * Helper function to build page schema
 * Extracted and optimized to avoid recreation on every render
 * @param {string} keyword - The original keyword from the route
 * @param {string} normalizedSlug - The normalized slug from URL (for image paths)
 * @param {object} pageType - The page Type
 * @param {array} reviewSchema - The review schema array
 * @param {object} metadata - The metadata object
 */
export const buildPageSchema = (
	keyword,
	normalizedSlug,
	pageType,
	reviewSchema,
	metadata
) => {
	const pageUrl = buildPSPUrl(keyword);

	let pageName;
	let serviceName;

	if (pageType === "Storage Units with Electricity") {
		pageName = pageType;
		serviceName = "Storage with Electricity";
	} else if (pageType === "Storage Units with Free Truck Rentals") {
		pageName = pageType;
		serviceName = "Storage with Free Truck Rentals";
	} else {
		pageName = `${pageType} Storage Units`;
		serviceName = `${pageType} Storage`;
	}

	return {
		"@context": "https://schema.org",
		"@type": "WebPage",
		name: metadata?.title,
		url: pageUrl,
		description: metadata?.description,
		inLanguage: "en-US",
		isPartOf: {
			"@type": "WebSite",
			"@id": "https://www.storage.com/#website"
		},
		about: {
			"@type": "Service",
			serviceType: serviceName,
			provider: {
				"@type": "Organization",
				name: "Storage",
				aggregateRating: {
					"@type": "AggregateRating",
					ratingValue: "4.4",
					reviewCount: "178",
					bestRating: "5",
					worstRating: "1"
				}
			}
		},
		primaryImageOfPage: {
			"@type": "ImageObject",
			url: `https://www.storage.com/images/psp/hero/${normalizedSlug}-hero.webp`,
			width: 1200,
			height: 630
		},
		breadcrumb: {
			"@type": "BreadcrumbList",
			itemListElement: [
				{
					"@type": "ListItem",
					position: 1,
					name: "Home",
					item: "https://www.storage.com/"
				},
				{
					"@type": "ListItem",
					position: 2,
					name: pageName,
					item: pageUrl
				}
			]
		},
		review: reviewSchema
	};
};

export const PSP_PAGE_BY_KEY = {
	rv: PAGE_SUB_TYPES.RV_LANDING,
	car: PAGE_SUB_TYPES.CAR_LANDING,
	boat: PAGE_SUB_TYPES.BOAT_LANDING,
	"climate-controlled": PAGE_SUB_TYPES.CLIMATE_CONTROLLED_LANDING,
	portable: PAGE_SUB_TYPES.PORTABLE_LANDING,
	motorcycle: PAGE_SUB_TYPES.MOTORCYCLE_LANDING,
	business: PAGE_SUB_TYPES.BUSINESS_LANDING,
	wine: PAGE_SUB_TYPES.WINE_LANDING,
	atv: PAGE_SUB_TYPES.ATV_LANDING,
	bicycle: PAGE_SUB_TYPES.BICYCLE_LANDING,
	"dirt-bike": PAGE_SUB_TYPES.DIRT_BIKE_LANDING,
	"golf-cart": PAGE_SUB_TYPES.GOLF_CART_LANDING,
	college: PAGE_SUB_TYPES.COLLEGE_LANDING,
	truck: PAGE_SUB_TYPES.TRUCK_LANDING,
	"jet-ski": PAGE_SUB_TYPES.JET_SKI_LANDING,
	kayak: PAGE_SUB_TYPES.KAYAK_LANDING,
	military: PAGE_SUB_TYPES.MILITARY_LANDING,
	"drive-up": PAGE_SUB_TYPES.DRIVE_UP_LANDING,
	"24-hour": PAGE_SUB_TYPES.TWENTY_FOUR_HOUR_LANDING,
	vehicle: PAGE_SUB_TYPES.VEHICLE_LANDING,
	"storage-with-electricity": PAGE_SUB_TYPES.STORAGE_WITH_ELECTRICITY_LANDING,
	"storage-101": PAGE_SUB_TYPES.STORAGE_101_LANDING
};
